import React from 'react';
import { useReactiveVar } from '@apollo/client';
import { Form, OnSubmit } from '@cvent/carina/components/Forms';
import { useTranslate } from 'nucleus-text';

import { useFormStyle } from '@hooks/style/useFormStyle';
import { ValidateOnLanguageChange } from '@components/helpers/FormHelpers';
import { titleVar } from '@components/framework/ReactiveVars';
import { FormElementContainer } from '@components/containers/FormElementContainer';
import { FormTextboxContainer } from '@components/containers/FormTextboxContainer';
import { FormTitleContainer } from '@components/containers/FormTitleContainer';
import { TextWithLinkContainer } from '@components/containers/TextWithLinkContainer';
import { HorizontalDivider } from '@components/display/HorizontalDivider';
import { Alert } from '@components/toasts/Alert';
import { ProductMenu } from '@components/menus/ProductMenu';
import { UsernameTextbox } from '@components/inputFields/UsernameTextbox';
import { PasswordTextbox } from '@components/inputFields/PasswordTextbox';
import { ForgotPasswordTextLink } from '@components/textFields/ForgotPasswordTextLink';
import { CustomText } from '@components/textFields/CustomText';
import { SubmitButton } from '@components/buttons/SubmitButton';
import { SsoButton } from '@components/buttons/SsoButton';
import { CoreTooltip } from '@components/display/Tooltip';
import { Title } from '@components/textFields/Title';

type LoginFormProps = {
  testID: string;
  initialValues?: Record<string, string>;
  onSubmit: OnSubmit<Record<string, string>, 'form'>;
  children: {
    title?: React.ReactNode;
    subtitle?: React.ReactNode;
    secondaryText?: React.ReactNode;
    inputFields?: React.ReactNode[];
    submitText?: string;
    aboveDivider?: React.ReactNode[];
    divider?: React.ReactNode;
    belowDivider?: React.ReactNode[];
  };
};

const defaults = {
  initialValues: { username: '', password: '' }
};

export const LoginForm = ({
  testID,
  initialValues = defaults.initialValues,
  onSubmit,
  children
}: LoginFormProps): JSX.Element => {
  const { translate } = useTranslate();
  const { centerText, form } = useFormStyle();
  const loginProduct = useReactiveVar(titleVar);

  const {
    title = (
      <TextWithLinkContainer testID={testID}>
        {{
          text: <Title testID={testID} text={translate('form_title_mpl', { targetProduct: loginProduct })} />,
          link: <ProductMenu testID={testID} />,
          tooltip: <CoreTooltip testID={testID} />
        }}
      </TextWithLinkContainer>
    ),
    subtitle,
    secondaryText,
    inputFields = [<UsernameTextbox testID={testID} />, <PasswordTextbox testID={testID} />],
    submitText = translate('form_login'),
    aboveDivider = [
      <CustomText
        testID={`${testID}_dividerText`}
        as={'p'}
        text={translate('form_conditional')}
        typeStyle={centerText}
      />,
      <SsoButton testID={testID} />
    ],
    divider = <HorizontalDivider testID={testID} />,
    belowDivider = [<ForgotPasswordTextLink testID={testID} />]
  } = children;

  return (
    <Form testID={`${testID}_form`} initialValues={initialValues} onSubmit={onSubmit} css={form}>
      {title && <FormTitleContainer testID={testID}>{title}</FormTitleContainer>}
      {subtitle && <FormTitleContainer testID={testID}>{subtitle}</FormTitleContainer>}
      {secondaryText && <FormTitleContainer testID={testID}>{secondaryText}</FormTitleContainer>}
      <FormElementContainer testID={testID}>
        <Alert testID={testID} />
      </FormElementContainer>
      <ValidateOnLanguageChange />
      {inputFields &&
        inputFields.map((el, idx) => (
          <FormTextboxContainer key={`inputField_${idx}`} testID={testID}>
            {el}
          </FormTextboxContainer>
        ))}
      <FormElementContainer testID={testID}>
        <SubmitButton testID={testID} text={submitText} />
      </FormElementContainer>
      {aboveDivider &&
        aboveDivider.map((el, idx) => (
          <FormElementContainer key={`aboveDivider_${idx}`} testID={testID}>
            {el}
          </FormElementContainer>
        ))}
      {divider && <FormElementContainer testID={testID}>{divider}</FormElementContainer>}
      {belowDivider &&
        belowDivider.map((el, idx) => (
          <FormElementContainer key={`belowDivider_${idx}`} testID={testID}>
            {el}
          </FormElementContainer>
        ))}
    </Form>
  );
};
